import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, combineLatest } from 'rxjs';
import { AccountsService } from '@services/accounts.service';
import { AuthenticateAdminService } from '@services/dws';
import { first, map } from 'rxjs/operators';
import { isPublicAppPath } from '@services/permissions/landing-route.service';

@Injectable({
  providedIn: 'root'
})
export class OnlyAuthorizedGuard implements CanActivate {
  constructor(
    private accounts: AccountsService,
    private adminAuth: AuthenticateAdminService,
    private router: Router
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    if (this.isAdminRoute(next)) {
      return combineLatest([
        this.adminAuth.authorization$.pipe(first()),
        this.accounts.authorization$.pipe(first()),
      ]).pipe(map(([adminAuth, accountsAuth]) => {
        const ok = !!adminAuth || !!(accountsAuth?.is_admin && accountsAuth?.is_winery);
        return ok ? true : this.router.parseUrl('/admin/login');
      }));
    }

    return this.accounts.login$.pipe(
      first(),
      map(login => {
        if (!login) return this.loginWithRedirect(state.url);
        if (this.accounts.isAccountSuspended(login)) {
          this.accounts.logout();
          return this.router.parseUrl('/login?suspended=1');
        }
        return true;
      })
    );
  }

  private isAdminRoute(route: ActivatedRouteSnapshot): boolean {
    for (let r: ActivatedRouteSnapshot | null = route; r; r = r.parent) {
      if (r.data['admin'] === true) return true;
    }
    return false;
  }

  private loginWithRedirect(attemptedUrl: string): UrlTree {
    if (!attemptedUrl || isPublicAppPath(attemptedUrl)) {
      return this.router.parseUrl("/login");
    }
    return this.router.createUrlTree(["/login"], { queryParams: { redirectTo: attemptedUrl } });
  }
}
